import { useSelector } from 'react-redux';
import {
  selectOverviewCountByUiFilterKind,
  selectRecordsByUiFilterKind,
} from '@mm/state/selectors/campaignsOverviewSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';
import { usePhoneNumber } from './use-phone-number';

export const useGreenCueMarkers = () => {
  const possibleCuesCount = 7;

  const { isSmsSupported } = usePhoneNumber();
  const fbPage = useSelector(activeFacebookPageSelector);

  // Note: the !isSmsSupported is used here to reduce the number of available cues
  // for people who do not have sms available to them. Thus, marking it as if it is activated/used.
  const isSmsActivated = !isSmsSupported || !!fbPage?.smsActivated;
  const isWebChatActivated = !!fbPage?.webChatActivated;
  const isCommentGuardActivated = !!fbPage?.commentGuardActivated;

  const hasDripCampaigns = !!useSelector((state: StoreState) =>
    selectOverviewCountByUiFilterKind(state, 'drip_campaigns'),
  );
  const campaigns = useSelector((state: StoreState) =>
    selectRecordsByUiFilterKind(state, 'all'),
  );
  const hasActivedDripCampaign = !!campaigns?.campaigns.filter(
    c => c.kind === 'drip' && (c.active || c.status !== 'queued'),
  ).length;

  const isDripCampaignsActivated = hasDripCampaigns && hasActivedDripCampaign;
  const isChatBlasterActivated = !!fbPage?.blasterActivated;

  const isZapierActivated = !!fbPage?.zapierActivated;
  const isNotificationWidgetActivated = !!fbPage?.notificationWidgetActivated;

  const featuresHash = {
    isSmsActivated,
    isWebChatActivated,
    isCommentGuardActivated,
    isChatBlasterActivated,
    isDripCampaignsActivated,
    isZapierActivated,
    isNotificationWidgetActivated,
  };

  const orderedByPreferenceFeatures = [
    'webchat',
    'comment_guard',
    'drip_campaign',
    'chat_blaster',
    'zapier',
  ];

  const [nextFeature, ...rest] = orderedByPreferenceFeatures.filter(f => {
    return (
      (f === 'webchat' && !isWebChatActivated) ||
      (f === 'comment_guard' && !isCommentGuardActivated) ||
      (f === 'drip_campaign' && !isDripCampaignsActivated) ||
      (f === 'chat_blaster' && !isChatBlasterActivated) ||
      (f === 'zapier' && !isZapierActivated)
    );
  });

  const activatedFeaturesCount = Object.values(featuresHash).reduce(
    (acc, val) => acc + Number(val),
    0,
  );

  const numberOfCuesLeft = possibleCuesCount - activatedFeaturesCount;

  const areAllChatStartersActivated =
    isSmsActivated && isWebChatActivated && isCommentGuardActivated;

  const areAllCampaignsActivated =
    isDripCampaignsActivated && isChatBlasterActivated;

  return {
    numberOfCuesLeft,
    ...featuresHash,
    areAllChatStartersActivated,
    areAllCampaignsActivated,
    nextFeature,
  };
};
